import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api, { errMsg } from '../lib/api';
import { PageHead, Field, Tgl, ImagePicker, inp } from './components';

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/settings');
      setS(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setHero = (k, v) => setS((p) => ({ ...p, hero: { ...(p.hero || {}), [k]: v } }));
  const setGate = (k, v) => setS((p) => ({ ...p, gate: { ...(p.gate || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', s);
      toast.success('Site settings saved — live immediately');
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!s) return <div className="h-40 bg-white animate-pulse border hairline" />;

  return (
    <div data-testid="admin-settings">
      <PageHead title="Site Settings" sub="Brand, contact channels, homepage and notices. No code changes needed." />
      <div className="space-y-6 max-w-3xl">
        <section className="border hairline bg-white p-6 space-y-4">
          <p className="font-display font-bold uppercase text-lg">Brand</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Brand name"><input data-testid="set-brand" value={s.brand_name || ''} onChange={(e) => set('brand_name', e.target.value)} className={inp} /></Field>
            <Field label="Tagline"><input data-testid="set-tagline" value={s.tagline || ''} onChange={(e) => set('tagline', e.target.value)} className={inp} /></Field>
          </div>
          <Field label="Announcement bar (top of every page)"><input data-testid="set-announcement" value={s.announcement_bar || ''} onChange={(e) => set('announcement_bar', e.target.value)} className={inp} /></Field>
          <Field label="Research notice"><input data-testid="set-research-notice" value={s.research_notice || ''} onChange={(e) => set('research_notice', e.target.value)} className={inp} /></Field>
          <Field label="Footer text"><textarea data-testid="set-footer-text" rows={3} value={s.footer_text || ''} onChange={(e) => set('footer_text', e.target.value)} className={inp} /></Field>
        </section>

        <section className="border hairline bg-white p-6 space-y-4">
          <p className="font-display font-bold uppercase text-lg">Contact channels</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="General email"><input data-testid="set-email-general" value={s.email_general || ''} onChange={(e) => set('email_general', e.target.value)} className={inp} /></Field>
            <Field label="Support email"><input data-testid="set-email-support" value={s.email_support || ''} onChange={(e) => set('email_support', e.target.value)} className={inp} /></Field>
            <Field label="Orders email"><input data-testid="set-email-orders" value={s.email_orders || ''} onChange={(e) => set('email_orders', e.target.value)} className={inp} /></Field>
            <Field label="WhatsApp URL"><input data-testid="set-whatsapp" value={s.whatsapp_url || ''} onChange={(e) => set('whatsapp_url', e.target.value)} className={inp} placeholder="https://wa.me/…" /></Field>
            <Field label="Telegram public URL (leave blank to hide)"><input data-testid="set-telegram" value={s.telegram_url || ''} onChange={(e) => set('telegram_url', e.target.value)} className={inp} placeholder="https://t.me/…" /></Field>
          </div>
        </section>

        <section className="border hairline bg-white p-6 space-y-4">
          <p className="font-display font-bold uppercase text-lg">Homepage hero</p>
          <Field label="Eyebrow"><input data-testid="set-hero-eyebrow" value={s.hero?.eyebrow || ''} onChange={(e) => setHero('eyebrow', e.target.value)} className={inp} /></Field>
          <Field label="Title"><input data-testid="set-hero-title" value={s.hero?.title || ''} onChange={(e) => setHero('title', e.target.value)} className={inp} /></Field>
          <Field label="Subtitle"><textarea data-testid="set-hero-subtitle" rows={2} value={s.hero?.subtitle || ''} onChange={(e) => setHero('subtitle', e.target.value)} className={inp} /></Field>
          <ImagePicker label="Hero image" value={s.hero?.image || ''} onChange={(v) => setHero('image', v)} testid="set-hero-image" />
        </section>

        <section className="border hairline bg-white p-6 space-y-4">
          <p className="font-display font-bold uppercase text-lg">Research / age gate</p>
          <Tgl label="Show acknowledgement gate to new visitors" checked={s.gate?.enabled !== false} onChange={(v) => setGate('enabled', v)} testid="set-gate-enabled" />
          <Field label="Gate title"><input data-testid="set-gate-title" value={s.gate?.title || ''} onChange={(e) => setGate('title', e.target.value)} className={inp} /></Field>
          <Field label="Gate body"><textarea data-testid="set-gate-body" rows={3} value={s.gate?.body || ''} onChange={(e) => setGate('body', e.target.value)} className={inp} /></Field>
        </section>

        <button data-testid="settings-save" onClick={save} disabled={saving}
          className="bg-[#0B0F12] text-white px-8 py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
          {saving ? 'Saving…' : 'Save all settings'}
        </button>
      </div>
    </div>
  );
}

export function AdminSocial() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/settings').then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="h-40 bg-white animate-pulse border hairline" />;
  const links = s.social_links || {};
  const setLink = (k, v) => setS((p) => ({ ...p, social_links: { ...(p.social_links || {}), [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/settings', { social_links: s.social_links, whatsapp_url: s.whatsapp_url, telegram_url: s.telegram_url }); toast.success('Social links saved'); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="admin-social">
      <PageHead title="Social Links" sub="Shown in the footer and contact page. Blank links are hidden." />
      <div className="border hairline bg-white p-6 space-y-4 max-w-xl">
        {[['whatsapp_url', 'WhatsApp URL'], ['telegram_url', 'Telegram public URL'], ['instagram', 'Instagram'], ['x', 'X / Twitter'], ['facebook', 'Facebook'], ['linkedin', 'LinkedIn']].map(([k, l]) => (
          <Field key={k} label={l}>
            {k === 'whatsapp_url' || k === 'telegram_url'
              ? <input data-testid={`social-${k}`} value={s[k] || ''} onChange={(e) => setS((p) => ({ ...p, [k]: e.target.value }))} className={inp} />
              : <input data-testid={`social-${k}`} value={links[k] || ''} onChange={(e) => setLink(k, e.target.value)} className={inp} />}
          </Field>
        ))}
        <button data-testid="social-save" onClick={save} disabled={saving}
          className="bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
          {saving ? 'Saving…' : 'Save links'}
        </button>
      </div>
    </div>
  );
}
